import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Grid, Chip, Switch, FormControlLabel,
  Alert, IconButton, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TablePagination, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, PlayArrow, Stop } from '@mui/icons-material';
import axios from 'axios';

function TabPanel({ children, value, index }) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

const COLUMNS = [
  { id: 'strategy_name', label: 'Name' },
  { id: 'strategy_type', label: 'Type' },
  { id: 'symbol', label: 'Symbol' },
  { id: 'exchange', label: 'Exchange' },
  { id: 'is_active', label: 'Status' },
  { id: 'modes', label: 'Modes', disableSort: true },
  { id: 'actions', label: '', disableSort: true },
];

const PAGE_OPTS = [10, 20, 50, 100, -1];

export default function StrategyManager({ apiUrl }) {
  const [strategies, setStrategies] = useState([]);
  const [total, setTotal] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [formData, setFormData] = useState({
    strategy_name: '', strategy_type: 'tradingview', description: '',
    exchange: 'NSE', symbol: '', paper_trade_enabled: true, real_trade_enabled: false,
  });
  const [settingsData, setSettingsData] = useState({
    take_profit: 100, stop_loss: 50, trailing_stop_loss_trigger: 20,
    trailing_stop_loss_move: 10, lot_size: 1, max_positions: 1, threshold_points: 15,
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStrategies = useCallback(async () => {
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage === -1 ? 1000 : rowsPerPage,
        sort_by: orderBy,
        sort_order: order,
      };
      const res = await axios.get(`${apiUrl}/strategies/`, { headers, params });
      setStrategies(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { setError('Failed to fetch strategies'); }
  }, [apiUrl, page, rowsPerPage, orderBy, order]);

  useEffect(() => { fetchStrategies(); }, [fetchStrategies]);

  const handleSort = (col) => {
    const isAsc = orderBy === col && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(col);
    setPage(0);
  };

  const handleCreateStrategy = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      if (editMode && selectedStrategy) {
        await axios.put(`${apiUrl}/strategies/${selectedStrategy.id}`, {
          strategy_name: formData.strategy_name,
          strategy_type: formData.strategy_type,
          description: formData.description,
          exchange: formData.exchange,
          symbol: formData.symbol,
          paper_trade_enabled: formData.paper_trade_enabled,
          real_trade_enabled: formData.real_trade_enabled,
        }, { headers });
        await axios.put(`${apiUrl}/strategies/${selectedStrategy.id}/settings`, settingsData, { headers });
        setSuccess('Strategy updated!');
      } else {
        const res = await axios.post(`${apiUrl}/strategies/`, formData, { headers });
        await axios.post(`${apiUrl}/strategies/settings`, { strategy_id: res.data.id, ...settingsData }, { headers });
        setSuccess('Strategy created!');
      }
      setOpenDialog(false); fetchStrategies(); resetForm();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to save';
      setError(detail);
    }
    finally { setLoading(false); }
  };

  const handleEditStrategy = async (strategy) => {
    setEditMode(true); setSelectedStrategy(strategy);
    setFormData({
      strategy_name: strategy.strategy_name, strategy_type: strategy.strategy_type,
      description: strategy.description || '', exchange: strategy.exchange,
      symbol: strategy.symbol, paper_trade_enabled: strategy.paper_trade_enabled,
      real_trade_enabled: strategy.real_trade_enabled,
    });
    try {
      const res = await axios.get(`${apiUrl}/strategies/${strategy.id}/settings`, { headers });
      if (res.data) setSettingsData({
        take_profit: res.data.take_profit || 100, stop_loss: res.data.stop_loss || 50,
        trailing_stop_loss_trigger: res.data.trailing_stop_loss_trigger || 20,
        trailing_stop_loss_move: res.data.trailing_stop_loss_move || 10,
        lot_size: res.data.lot_size || 1, max_positions: res.data.max_positions || 1,
        threshold_points: res.data.threshold_points || 15,
      });
    } catch {}
    setOpenDialog(true);
  };

  const handleToggle = async (id, isActive) => {
    try {
      await axios.put(`${apiUrl}/strategies/${id}`, { is_active: !isActive }, { headers });
      fetchStrategies();
    } catch { setError('Failed to toggle'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this strategy?')) return;
    try {
      await axios.delete(`${apiUrl}/strategies/${id}`, { headers });
      fetchStrategies(); setSuccess('Strategy deleted');
    } catch { setError('Failed to delete'); }
  };

  const resetForm = () => {
    setFormData({ strategy_name: '', strategy_type: 'tradingview', description: '', exchange: 'NSE', symbol: '', paper_trade_enabled: true, real_trade_enabled: false });
    setSettingsData({ take_profit: 100, stop_loss: 50, trailing_stop_loss_trigger: 20, trailing_stop_loss_move: 10, lot_size: 1, max_positions: 1, threshold_points: 15 });
    setEditMode(false); setSelectedStrategy(null); setTab(0);
  };

  return (
    <Box data-testid="strategy-manager">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Strategy Management</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => { resetForm(); setOpenDialog(true); }}
          data-testid="create-strategy-btn">Create Strategy</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                    {col.disableSort ? col.label : (
                      <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'}
                        onClick={() => handleSort(col.id)}>{col.label}</TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {strategies.length === 0 ? (
                <TableRow><TableCell colSpan={7}><Alert severity="info">No strategies. Create one to start.</Alert></TableCell></TableRow>
              ) : strategies.map((s) => (
                <TableRow key={s.id} data-testid={`strategy-row-${s.id}`} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{s.strategy_name}</Typography>
                    {s.description && <Typography variant="caption" color="text.secondary">{s.description}</Typography>}
                  </TableCell>
                  <TableCell><Chip label={s.strategy_type.toUpperCase()} size="small" variant="outlined" /></TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>{s.symbol}</Typography></TableCell>
                  <TableCell>{s.exchange}</TableCell>
                  <TableCell>
                    <Chip label={s.is_active ? 'Active' : 'Inactive'} size="small"
                      color={s.is_active ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      {s.paper_trade_enabled && <Chip label="Paper" size="small" variant="outlined" />}
                      {s.real_trade_enabled && <Chip label="Real" size="small" color="primary" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0}>
                      <Tooltip title={s.is_active ? 'Deactivate' : 'Activate'}>
                        <IconButton size="small" onClick={() => handleToggle(s.id, s.is_active)}
                          color={s.is_active ? 'error' : 'success'}>
                          {s.is_active ? <Stop sx={{ fontSize: 18 }} /> : <PlayArrow sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleEditStrategy(s)}>
                          <Edit sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(s.id)}>
                          <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={PAGE_OPTS.map(v => v === -1 ? { value: -1, label: 'All' } : v)}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          data-testid="strategy-pagination"
        />
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1.5 }}>{editMode ? 'Edit Strategy' : 'Create Strategy'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 1 }}>
            <Tab label="Basic" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }} />
            <Tab label="Settings" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }} />
          </Tabs>
          <TabPanel value={tab} index={0}>
            <Grid container spacing={1.5}>
              <Grid item xs={12}><TextField fullWidth size="small" label="Strategy Name" value={formData.strategy_name}
                onChange={(e) => setFormData({ ...formData, strategy_name: e.target.value })} required
                data-testid="strategy-name-input" /></Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small"><InputLabel>Type</InputLabel>
                  <Select value={formData.strategy_type} label="Type"
                    onChange={(e) => setFormData({ ...formData, strategy_type: e.target.value })}
                    data-testid="strategy-type-select">
                    <MenuItem value="tradingview">TradingView</MenuItem>
                    <MenuItem value="chartink">Chartink</MenuItem>
                    <MenuItem value="arbitrage">Arbitrage</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small"><InputLabel>Exchange</InputLabel>
                  <Select value={formData.exchange} label="Exchange"
                    onChange={(e) => setFormData({ ...formData, exchange: e.target.value })}
                    data-testid="strategy-exchange-select">
                    {['NSE', 'BSE', 'NFO', 'MCX'].map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Symbol" value={formData.symbol}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value })} required
                data-testid="strategy-symbol-input" /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} /></Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch size="small" checked={formData.paper_trade_enabled}
                  onChange={(e) => setFormData({ ...formData, paper_trade_enabled: e.target.checked })} />}
                  label={<Typography variant="body2">Paper Trading</Typography>} />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch size="small" checked={formData.real_trade_enabled}
                  onChange={(e) => setFormData({ ...formData, real_trade_enabled: e.target.checked })} />}
                  label={<Typography variant="body2">Real Trading</Typography>} />
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <Grid container spacing={1.5}>
              {[
                { key: 'take_profit', label: 'Take Profit (pts)' },
                { key: 'stop_loss', label: 'Stop Loss (pts)' },
                { key: 'trailing_stop_loss_trigger', label: 'TSL Trigger (pts)' },
                { key: 'trailing_stop_loss_move', label: 'TSL Move (pts)' },
                { key: 'lot_size', label: 'Lot Size', int: true },
                { key: 'max_positions', label: 'Max Positions', int: true },
              ].map(({ key, label, int }) => (
                <Grid item xs={6} key={key}>
                  <TextField fullWidth size="small" type="number" label={label} value={settingsData[key]}
                    onChange={(e) => setSettingsData({ ...settingsData, [key]: int ? parseInt(e.target.value) : parseFloat(e.target.value) })} />
                </Grid>
              ))}
              {formData.strategy_type === 'arbitrage' && (
                <Grid item xs={12}><TextField fullWidth size="small" type="number" label="Arbitrage Threshold (pts)"
                  value={settingsData.threshold_points}
                  onChange={(e) => setSettingsData({ ...settingsData, threshold_points: parseFloat(e.target.value) })}
                  helperText="Min price difference to trigger" /></Grid>
              )}
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); resetForm(); }}>Cancel</Button>
          <Button onClick={handleCreateStrategy} variant="contained" disabled={loading || !formData.strategy_name || !formData.symbol}
            data-testid="save-strategy-btn">
            {loading ? 'Saving...' : editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
